import { useState } from "react";
import styles from "./TotpVerificationForm.module.css";
import CareHiresLogo from "../../../../assets/img/CH_New_Logo.png";
import BlackBusinessman from "../../../../assets/img/BlackBusinessman.svg";
import { Button, Card, Col, Form, Input, Row } from "antd";
import { TotpMultiFactorGenerator } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../Toaster/Toaster";
import { setCookie } from "../../../../utils/cookies";
import { getUserCompaniesByUID, getUserDataByUID, updateLastRequestTime, userSignOut, recordLastSignedIn } from "../../../../services/auth.service";
import { getDomain } from "tldts";
import { datadogRum } from "@datadog/browser-rum";
import { isMobile } from "../../../../utils/device";
import { getUserImages } from "../../../../services/common.service";
import OrganizationSwitcher from "../../../OrganizationSwitcher/OrganizationSwitcher";
import { isAxiosError } from "axios";
import { getDeviceFingerprint } from "../../../../utils/fingerprint";
import type { AccountFlaggedError } from "../../../../types/security";

function TotpVerificationForm(props: any) {
  const resolver = props.resolver;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number>(0);
  const [userData, setUserData] = useState<any>(null);
  const [userCompanies, setUserCompanies] = useState<any>(null);
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] = useState(false);
  const cookieName = "ch_token";

  const getDomainUrl = () => {
    return getDomain(window.location.href) || "";
  };

  const handleWorkerOrMobileLogin = () => {
    if (isMobile()) {
      showToast("Id app doesn't support access from mobile devices.", {
        type: "error",
      });
      setLoading(false);
      setTimeout(() => {
        userSignOut();
      }, 3000);
      return true;
    }

    return false;
  };

  const verify = async (values: any) => {
    setLoading(true);
    const otpFromAuthenticator = values.verificationCode;
    console.log(otpFromAuthenticator);
    console.log(
      resolver.hints.filter(
        (hint: any) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      )[0].uid
    );
    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      resolver.hints.filter(
        (hint: any) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      )[0].uid,
      otpFromAuthenticator
    );
    try {
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      if (userCredential) {
        userCredential.user
          ?.getIdTokenResult()
          .then(
            async (idTokenResult: {
              token: any;
              expirationTime: string | number | Date;
              claims: any;
            }) => {
              showToast("Successfully verified", { type: "success" });
              const token = idTokenResult.token;
              const expirationTime = new Date(idTokenResult.expirationTime);
              const isWorker = idTokenResult.claims["worker"] == true;
              if(isWorker) {
                showToast("Access denied. Please contact admin for account setup.", { type: "error" });
              }
              const expiresInDays =
                (expirationTime.getTime() - new Date().getTime()) /
                (1000 * 60 * 60 * 24);
              setCookie("firebase_token", token, expiresInDays, getDomainUrl());
              setExpiresInDays(expiresInDays);

              try {
                const response = await updateLastRequestTime(token);
                if (response.success && response.data) {
                  const user = userCredential.user?.toJSON() as {
                    uid: string;
                    stsTokenManager: { accessToken: string };
                    email: string | undefined;
                    fullName: string | undefined;
                  };

                  // Start recording user sessions
                  datadogRum.setUser({
                    id: user.uid,
                    name: user.fullName,
                    email: user.email,
                  });
                  datadogRum.startSessionReplayRecording();

                  // Security check: device fingerprint + login telemetry before issuing ch_token
                  const fingerprint = await getDeviceFingerprint();
                  try {
                    const securityResponse = await recordLastSignedIn(user.uid, token, fingerprint);
                    if (securityResponse.data?.deviceBlocked) {
                      userSignOut();
                      props.onDeviceBlocked?.();
                      setLoading(false);
                      return;
                    }
                    if (securityResponse.data?.passwordExpired && securityResponse.data?.passwordChangeToken) {
                      userSignOut();
                      window.location.href = `/set-password?token=${securityResponse.data.passwordChangeToken}`;
                      return;
                    }
                    if (securityResponse.data?.isNewDevice) {
                      showToast("A security verification email has been sent to your inbox.", { type: "warning" });
                    }
                  } catch (securityError: unknown) {
                    if (
                      isAxiosError(securityError) &&
                      securityError.response?.status === 403 &&
                      (securityError.response.data as AccountFlaggedError).accountFlagged
                    ) {
                      userSignOut();
                      props.onAccountFlagged?.();
                      setLoading(false);
                      return;
                    }
                    userSignOut();
                    showToast("Unable to complete sign-in. Please try again.", { type: "error" });
                    setLoading(false);
                    return;
                  }

                  if (user) {
                    const fetchResponse = await fetch(
                      `${import.meta.env.VITE_FIREBASE_API_URL}/auth/access_token?uid=${user.uid}`,
                      {
                        method: "GET",
                        headers: {
                          "Content-Type": "application/json",
                        },
                      }
                    );

                    const data = await fetchResponse.json();

                    if (data.success) {
                      const accessToken = data.data;
                      setCookie(cookieName, accessToken, expiresInDays, getDomainUrl());

                      if (props.redirectUrl) {

                        const userData = await getUserDataByUID(user.uid, token);

                        if (userData.data.userGroup == "Provider") {
                          let companies: any = [];
                          const userCompaniesResponse = await getUserCompaniesByUID(userData.data?._id, "provider", userData.data?.provider?._id, token);
                          const subCompanies = userCompaniesResponse?.data;
                          const providers = subCompanies.filter((x: { provider: any; }) => x.provider).map((company: { provider: { id: any; companyName: any; }; careHomes: any; isAgreedToAgencyAndProviderTermsAndConditions: any; isAgreedToCareHiresTermsAndConditions: any; isAuthorizer: any; isDefault: any; isPrimaryUser: any; jobTitle: any; phone: any; userType: any; apps: any; }) => ({ id: company.provider.id, name: company.provider.companyName, careHomes: company.careHomes, isAgreedToAgencyAndProviderTermsAndConditions: company.isAgreedToAgencyAndProviderTermsAndConditions, isAgreedToCareHiresTermsAndConditions: company.isAgreedToCareHiresTermsAndConditions, isAuthorizer: company.isAuthorizer, isDefault: company.isDefault, isPrimaryUser: company.isPrimaryUser, jobTitle: company.jobTitle, phone: company.phone, provider: company.provider, userType: company.userType, isProvider: true, apps: company.apps }));
                          if (providers.length > 0) {
                            const res = await getUserImages("provider", providers.map((x: { id: any; }) => x.id), token);
                            if (res.data.length > 0) {
                              for (const img of res.data) {
                                const provider = providers.find((x: { id: any; }) => x.id === img.id);
                                if (provider) {
                                  provider.image = img.image;
                                }
                              }
                            }

                            companies = [...providers];
                            companies.forEach((c: any) => {
                              if (c.provider?._id === userData.data.provider?._id) {
                                c.isSelected = true;
                              } else {
                                c.isSelected = false
                              }
                            })
                          }
                          localStorage.setItem("companies", JSON.stringify(companies))
                          localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                          setCookie(
                            "ch_user_data",
                            JSON.stringify(userData.data),
                            expiresInDays,
                            getDomainUrl()
                          );

                        } else if (userData.data.userGroup == "Agency") {
                          let companies: any = [];
                          const userCompaniesResponse = await getUserCompaniesByUID(userData.data?._id, "agency", userData.data?.agency?._id, token);
                          const subCompanies = userCompaniesResponse?.data;
                          const agencies = subCompanies.filter((x: { agency: any; }) => x.agency).map((company: { agency: { id: any; businessName: any; }; locations: any; isAgreedToAgencyAndProviderTermsAndConditions: any; isAgreedToCareHiresTermsAndConditions: any; isAuthorizer: any; isDefault: any; isPrimaryUser: any; jobTitle: any; phone: any; userType: any; apps: any; }) => ({ id: company.agency.id, name: company.agency.businessName, locations: company.locations, isAgreedToAgencyAndProviderTermsAndConditions: company.isAgreedToAgencyAndProviderTermsAndConditions, isAgreedToCareHiresTermsAndConditions: company.isAgreedToCareHiresTermsAndConditions, isAuthorizer: company.isAuthorizer, isDefault: company.isDefault, isPrimaryUser: company.isPrimaryUser, jobTitle: company.jobTitle, phone: company.phone, agency: company.agency, userType: company.userType, isAgency: true, apps: company.apps }));
                          if (agencies.length > 0) {
                            const res = await getUserImages("agency", agencies.map((x: { id: any; }) => x.id), token);
                            if (res.data.length > 0) {
                              for (const img of res.data) {
                                const agency = agencies.find((x: { id: any; }) => x.id === img.id);
                                if (agency) {
                                  agency.image = img.image;
                                }
                              }
                            }
                            companies = [...agencies];
                            companies.forEach((c: any) => {
                              if (c.agency?._id === userData.data.agency?._id) {
                                c.isSelected = true;
                              } else {
                                c.isSelected = false
                              }
                            });
                          }
                          localStorage.setItem("companies", JSON.stringify(companies))
                          localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                          setCookie(
                            "ch_user_data",
                            JSON.stringify(userData.data),
                            expiresInDays,
                            getDomainUrl()
                          );
                        } else {
                          localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                          setCookie(
                            "ch_user_data",
                            JSON.stringify(userData.data),
                            expiresInDays,
                            getDomainUrl()
                          );
                        }
                        const pageName = props.extractPageName(
                          props.redirectUrl
                        );
                        window.location.href = `${props.redirectUrl}/${pageName}`;
                        window.location.href = props.redirectUrl;
                        return;
                      }

                      // if (handleWorkerOrMobileLogin()) {
                      //   return;
                      // }

                      const userData = await getUserDataByUID(user.uid, token);
                      if (userData.data.userGroup == "Provider") {
                        let companies: any = [];
                        const userCompaniesResponse = await getUserCompaniesByUID(userData.data?._id, "provider", userData.data?.provider?._id, token);
                        const subCompanies = userCompaniesResponse?.data;
                        const providers = subCompanies.filter((x: { provider: any; }) => x.provider).map((company: { provider: { id: any; companyName: any; }; careHomes: any; isAgreedToAgencyAndProviderTermsAndConditions: any; isAgreedToCareHiresTermsAndConditions: any; isAuthorizer: any; isDefault: any; isPrimaryUser: any; jobTitle: any; phone: any; userType: any; apps: any; }) => ({ id: company.provider.id, name: company.provider.companyName, careHomes: company.careHomes, isAgreedToAgencyAndProviderTermsAndConditions: company.isAgreedToAgencyAndProviderTermsAndConditions, isAgreedToCareHiresTermsAndConditions: company.isAgreedToCareHiresTermsAndConditions, isAuthorizer: company.isAuthorizer, isDefault: company.isDefault, isPrimaryUser: company.isPrimaryUser, jobTitle: company.jobTitle, phone: company.phone, provider: company.provider, userType: company.userType, isProvider: true, apps: company.apps }));
                        if (providers.length > 0) {
                          const res = await getUserImages("provider", providers.map((x: { id: any; }) => x.id), token);
                          if (res.data.length > 0) {
                            for (const img of res.data) {
                              const provider = providers.find((x: { id: any; }) => x.id === img.id);
                              if (provider) {
                                provider.image = img.image;
                              }
                            }
                          }

                          companies = [...providers];
                          companies.forEach((c: any) => {
                            if (c.provider?._id === userData.data.provider?._id) {
                              c.isSelected = true;
                            } else {
                              c.isSelected = false
                            }
                          })
                        }
                        localStorage.setItem("companies", JSON.stringify(companies))
                        if (userData.data?.provider?.parentCompany) {
                          setUserData(userData.data);
                          setShowOrganizationSwitcher(true);
                          setUserCompanies(companies);
                        } else {
                          localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                          setCookie(
                            "ch_user_data",
                            JSON.stringify(userData.data),
                            expiresInDays,
                            getDomainUrl()
                          );

                          navigate("/dashboard");
                        }
                      } else if (userData.data.userGroup == "Agency") {
                        let companies: any = [];
                        const userCompaniesResponse = await getUserCompaniesByUID(userData.data?._id, "agency", userData.data?.agency?._id, token);
                        const subCompanies = userCompaniesResponse?.data;
                        const agencies = subCompanies.filter((x: { agency: any; }) => x.agency).map((company: { agency: { id: any; businessName: any; }; locations: any; isAgreedToAgencyAndProviderTermsAndConditions: any; isAgreedToCareHiresTermsAndConditions: any; isAuthorizer: any; isDefault: any; isPrimaryUser: any; jobTitle: any; phone: any; userType: any; apps: any; }) => ({ id: company.agency.id, name: company.agency.businessName, locations: company.locations, isAgreedToAgencyAndProviderTermsAndConditions: company.isAgreedToAgencyAndProviderTermsAndConditions, isAgreedToCareHiresTermsAndConditions: company.isAgreedToCareHiresTermsAndConditions, isAuthorizer: company.isAuthorizer, isDefault: company.isDefault, isPrimaryUser: company.isPrimaryUser, jobTitle: company.jobTitle, phone: company.phone, agency: company.agency, userType: company.userType, isAgency: true, apps: company.apps }));
                        if (agencies.length > 0) {
                          const res = await getUserImages("agency", agencies.map((x: { id: any; }) => x.id), token);
                          if (res.data.length > 0) {
                            for (const img of res.data) {
                              const agency = agencies.find((x: { id: any; }) => x.id === img.id);
                              if (agency) {
                                agency.image = img.image;
                              }
                            }
                          }
                          companies = [...agencies];
                          companies.forEach((c: any) => {
                            if (c.agency?._id === userData.data.agency?._id) {
                              c.isSelected = true;
                            } else {
                              c.isSelected = false
                            }
                          });
                        }
                        localStorage.setItem("companies", JSON.stringify(companies))
                        if (userData.data?.agency?.parentCompany) {
                          setUserData(userData.data);
                          setShowOrganizationSwitcher(true);
                          setUserCompanies(companies);
                        } else {
                          localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                          setCookie(
                            "ch_user_data",
                            JSON.stringify(userData.data),
                            expiresInDays,
                            getDomainUrl()
                          );

                          navigate("/dashboard");
                        }
                      } else if (userData.data.userGroup == "Admin") {
                        localStorage.setItem("ch_user_data", JSON.stringify(userData.data));
                        setCookie(
                          "ch_user_data",
                          JSON.stringify(userData.data),
                          expiresInDays,
                          getDomainUrl()
                        );

                        navigate("/dashboard");
                      }


                    } else {
                      showToast("Failed to get access token", { type: "error" });
                      setLoading(false);
                    }
                  }
                }
              } catch (error) {
                console.error("Error updating last request time:", error);
              }
            }
          );
      }
    } catch (error: any) {
      setLoading(false);
      if (error.code === "auth/invalid-verification-code") {
        showToast("Invalid verification code", { type: "error" });
        return;
      }
      console.error(error);
      showToast(error.message, { type: "error" });
    }
  };

  return (
    <div className={styles["login-container"]}>
      {showOrganizationSwitcher && (
        <OrganizationSwitcher
          expiresInDays={expiresInDays}
          show={showOrganizationSwitcher}
          userData={userData}
          userCompanies={userCompanies}
        />
      )}
      {!showOrganizationSwitcher && (
        <>
          <img className="login-title-main" src={CareHiresLogo} alt="CareHires Logo" />
          <Row justify="center" align="middle">
            <Col md={24}>
              <Card bordered={false} className={styles["login-card"]}>
                <h3 className={styles["sign-in-heading"]}>
                  Multi-Factor Authentication
                </h3>
                <p className={styles["mfa-description"]}>
                  To help keep your account safe, Care Hires wants to
                </p>
                <p className={styles["mfa-description"]}>
                  make sure that it's really you trying to sign in
                </p>
                <img
                  src={BlackBusinessman}
                  className="login-logo"
                  alt="logo"
                  style={{ height: "12rem", width: "12rem" }}
                />
                <Row justify="center" align="middle">
                  <Card style={{ width: 500 }}>
                    <p className={styles["verify-code-text"]}>
                      Please open the Authenticator App and enter the code below. <br />
                      The Authenticator App will then generate a 6 digit code.
                    </p>
                    <p className={styles["verify-code-heading"]}>
                      Authentication Code
                    </p>
                    <Form
                      onFinish={verify}
                      layout="vertical"
                      className="login-form"
                    >
                      <Form.Item
                        name="verificationCode"
                        rules={[
                          {
                            required: true,
                            message: "Please input the verification code!",
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder="Enter the Verification Code"
                          className="custom-input"
                        />
                      </Form.Item>
                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          className={styles["verify-btn"]}
                          loading={loading}
                        >
                          Verify
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default TotpVerificationForm;
